import { lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Routes, Route, Navigate, useLocation, useRouteError } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import LoginPage from './pages/LoginPage';
import TicketsPage from './pages/TicketsPage';
import DiscussionsPage from './pages/DiscussionsPage';
import { conversationsRedirect } from './components/tickets/conversationsRedirect';

/** D-319 sec.9 - "אישורים חודשיים" became the "חוזי שירות" tab of "פירוט לוגים"; the old address (and its ?month=) opens it. */
function MonthlyApprovalsRedirect() {
  const location = useLocation();
  const month = new URLSearchParams(location.search).get('month');
  return <Navigate to={`/log-detail?tab=service${month ? `&month=${encodeURIComponent(month)}` : ''}`} replace />;
}

/** D-146 (Q-95) — "שיחות" is removed: its old links open the ticket / discussion chat or the lists. */
function ConversationsRedirect() {
  const location = useLocation();
  return <Navigate to={conversationsRedirect(location.search)} replace />;
}
import TicketDetailPage from './pages/TicketDetailPage';
import WorkLogsPage from './pages/WorkLogsPage';
import BillingPage from './pages/BillingPage';
import VoicePage from './pages/VoicePage';
import ProfilePage from './pages/ProfilePage';
import OrganizationPage from './pages/OrganizationPage';
import InternalWorkPage from './pages/InternalWorkPage';
import OrganizationsPage from './pages/OrganizationsPage';
import ProjectPage from './pages/ProjectPage';
import MilestonePage from './pages/MilestonePage';
import ProjectsPage from './pages/ProjectsPage';
const AllTasksPage = lazy(() => import('./pages/AllTasksPage'));
const TaskPage = lazy(() => import('./pages/TaskPage'));
const AllMilestonesPage = lazy(() => import('./pages/AllMilestonesPage'));
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import ScreenGate from './components/permissions/ScreenGate';
import { isPortalIdentity } from './components/permissions/screenAccess';
import { profileRedirectTarget } from './components/portal/portalShared';
import { usePermissions } from './hooks/usePermissions';
import ToastContainer from './components/ToastContainer';
import LiveAnnouncer from './components/ui/LiveAnnouncer';
import WorkspaceFeatureRoute from './components/workspace/WorkspaceFeatureRoute';
import { useHistoryTrail } from './hooks/useBackNavigation';
const ApiPlatformPage = lazy(() => import('./pages/ApiPlatformPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const IntegrationsPage = lazy(() => import('./pages/IntegrationsPage'));
const IntegrationDetailPage = lazy(() => import('./pages/IntegrationDetailPage'));
const BlocklistPage = lazy(() => import('./pages/BlocklistPage'));
const EmailTemplatesPage = lazy(() => import('./pages/EmailTemplatesPage'));
const SystemSettingsPage = lazy(() => import('./pages/SystemSettingsPage'));
const AuditTrailPage = lazy(() => import('./pages/AuditTrailPage'));
const AttendancePage = lazy(() => import('./pages/AttendancePage'));
const ContactsPage = lazy(() => import('./pages/ContactsPage'));
const LeadsPage = lazy(() => import('./pages/LeadsPage'));
const LeadDetailPage = lazy(() => import('./pages/LeadDetailPage'));
const BackupsPage = lazy(() => import('./pages/BackupsPage'));
const AdminDiagnosticsPage = lazy(() => import('./pages/AdminDiagnosticsPage'));
const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const QuotesPage = lazy(() => import('./pages/QuotesPage'));
const QuoteDetailPage = lazy(() => import('./pages/QuoteDetailPage'));
const MeetingsPage = lazy(() => import('./pages/MeetingsPage'));
const MeetingPage = lazy(() => import('./pages/MeetingPage'));
const CashFlowPage = lazy(() => import('./pages/CashFlowPage'));
const BugReportsPage = lazy(() => import('./pages/BugReportsPage'));
const MyBugReportsPage = lazy(() => import('./pages/MyBugReportsPage'));
const PermissionsPage = lazy(() => import('./pages/PermissionsPage'));
const PortalAccessPage = lazy(() => import('./pages/PortalAccessPage'));
const EmployeeProfilesPage = lazy(() => import('./pages/EmployeeProfilesPage'));
const EngagementsPage = lazy(() => import('./pages/EngagementsPage'));
const EngagementDetailPage = lazy(() => import('./pages/EngagementDetailPage'));
const DocumentsPage = lazy(() => import('./pages/DocumentsPage'));
const ProjectDeliveriesPage = lazy(() => import('./pages/ProjectDeliveriesPage'));
const EmployeeWorkspaceMockupPage = lazy(() => import('./pages/EmployeeWorkspaceMockupPage'));
const HomePage = lazy(() => import('./pages/HomePage'));
const WorkspaceCalendarPage = lazy(() => import('./pages/WorkspaceCalendarPage'));
const ObligationsPage = lazy(() => import('./pages/ObligationsPage'));
// P-10c (D-70) — monthly service-log approvals (OTE manager) and the employee's breakdown.
const LogDetailPage = lazy(() => import('./pages/LogDetailPage'));
const MyServiceLogPage = lazy(() => import('./pages/MyServiceLogPage'));
const MonthApprovalPage = lazy(() => import('./pages/MonthApprovalPage'));
const CustomerPortalPage = lazy(() => import('./pages/CustomerPortalPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
// P-38a (D-192 §3) — public: the RSVP link of a meeting invite.
const MeetingRsvpPage = lazy(() => import('./pages/MeetingRsvpPage'));

function LazyFallback() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

function CustomerPortalRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);
  // The customer portal is for customer_viewer; OTE staff may also open it but
  // only if they hold a portal_access grant (resolved in the portal context).
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/**
 * Home landing — every customer identity goes to the customer portal (SEC-2a,
 * D-91 ב5): a customer_viewer, and any login without an employee profile
 * (`is_ote_employee: false` from login, or `portalOnly` from the server),
 * whatever its role claim. The server refuses it every internal API.
 */
function HomeRedirect() {
  const user = useAuthStore((state) => state.user);
  const { portalOnly } = usePermissions();
  if (isPortalIdentity(user) || portalOnly) {
    return <Navigate to="/portal" replace />;
  }
  return (
    <Suspense fallback={<LazyFallback />}>
      <HomePage />
    </Suspense>
  );
}

/**
 * Q-DQ-114 - /profile: a customer identity never opens the internal shell. Its profile lives in the portal
 * ("הפרופיל שלי", /portal?tab=profile), and `?tab=` of the old link becomes the portal's `ptab`.
 * An OTE employee keeps the page exactly as before.
 */
function ProfileRoute() {
  const user = useAuthStore((state) => state.user);
  const { portalOnly } = usePermissions();
  const location = useLocation();
  if (isPortalIdentity(user) || portalOnly) {
    return <Navigate to={profileRedirectTarget(location.search)} replace />;
  }
  return <ProfilePage />;
}

function AppRoutes() {
  const location = useLocation();

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      {/* Public password recovery — outside the ProtectedRoute tree, like
          /login. /reset-password reads its token from the query string. */}
      <Route
        path="/forgot-password"
        element={<Suspense fallback={<LazyFallback />}><ResetPasswordPage /></Suspense>}
      />
      <Route
        path="/reset-password"
        element={<Suspense fallback={<LazyFallback />}><ResetPasswordPage /></Suspense>}
      />
      {/* P-38a (D-192 §3) — public RSVP from the invite e-mail (no login). */}
      <Route
        path="/meeting-rsvp"
        element={<Suspense fallback={<LazyFallback />}><MeetingRsvpPage /></Suspense>}
      />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <ErrorBoundary resetKey={location.pathname}>
              <Layout />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      >
        {/* Every screen goes through one guard: a closed screen's address returns home silently. */}
        <Route element={<ScreenGate />}>
          <Route
            index
            element={
              // Stage יב: a customer_viewer belongs in the customer portal, not
              // the OTE employee workspace.
              <HomeRedirect />
            }
          />
          <Route path="workspace" element={<Navigate to="/" replace />} />
          <Route
            path="workspace/calendar"
            element={
              <Suspense fallback={<LazyFallback />}>
                <WorkspaceFeatureRoute>
                  <WorkspaceCalendarPage />
                </WorkspaceFeatureRoute>
              </Suspense>
            }
          />
          {/* P-32b (D-186) — "לוח בקרה" is a tab of the home page; the old address opens that tab. */}
          <Route path="dashboard" element={<Navigate to="/?tab=dashboard" replace />} />
          {/* Design mockup with demo data: development builds only (never shipped to users). */}
          {import.meta.env.DEV && (
            <Route
              path="workspace-mockup"
              element={
                <Suspense fallback={<LazyFallback />}>
                  <EmployeeWorkspaceMockupPage />
                </Suspense>
              }
            />
          )}
          <Route path="tickets" element={<TicketsPage />} />
          {/* D-146 (Q-95) — "דיונים" is its own page; "שיחות" only redirects. */}
          <Route path="discussions" element={<DiscussionsPage />} />
          <Route path="conversations" element={<ConversationsRedirect />} />
          <Route path="tickets/:id" element={<TicketDetailPage />} />
          <Route path="organizations" element={<OrganizationsPage />} />
          <Route path="organizations/:id" element={<OrganizationPage />} />
          {/* D-321 - one place for the company's own work (no money, no portal). */}
          <Route path="internal-work" element={<InternalWorkPage />} />
          <Route path="projects" element={<Suspense fallback={<LazyFallback />}><ProjectsPage /></Suspense>} />
          <Route path="projects/:id" element={<ProjectPage />} />
          <Route path="milestones/:id" element={<MilestonePage />} />
          <Route
            path="tasks"
            element={<Suspense fallback={<LazyFallback />}><AllTasksPage /></Suspense>}
          />
          {/* P-06/F (D-61) — the "שלי" pages are gone: old links land on the general lists. */}
          <Route path="my-tasks" element={<Navigate to="/tasks" replace />} />
          <Route path="my-milestones" element={<Navigate to="/milestones" replace />} />
          <Route path="my-customer" element={<Navigate to="/organizations" replace />} />
          <Route path="my-organization" element={<Navigate to="/organizations" replace />} />
          {/* P-06/B (D-58, T-14) — the task page; the menu marks "משימות". */}
          <Route
            path="tasks/:id"
            element={<Suspense fallback={<LazyFallback />}><TaskPage /></Suspense>}
          />
          <Route
            path="milestones"
            element={<Suspense fallback={<LazyFallback />}><AllMilestonesPage /></Suspense>}
          />
          <Route
            path="work-logs"
            element={<WorkLogsPage />}
          />
          <Route
            path="billing"
            element={<BillingPage />}
          />
          <Route
            path="obligations"
            element={<Suspense fallback={<LazyFallback />}><ObligationsPage /></Suspense>}
          />
          <Route
            path="monthly-approvals"
            element={<MonthlyApprovalsRedirect />}
          />
          <Route
            path="log-detail"
            element={<Suspense fallback={<LazyFallback />}><LogDetailPage /></Suspense>}
          />
          <Route
            path="service-log/my"
            element={<Suspense fallback={<LazyFallback />}><MyServiceLogPage /></Suspense>}
          />
          <Route
            path="voice"
            element={<VoicePage />}
          />
          <Route
            path="contacts"
            element={<Suspense fallback={<LazyFallback />}><ContactsPage /></Suspense>}
          />
          <Route
            path="meetings"
            element={<Suspense fallback={<LazyFallback />}><MeetingsPage /></Suspense>}
          />
          <Route
            path="meetings/:id"
            element={<Suspense fallback={<LazyFallback />}><MeetingPage /></Suspense>}
          />
          <Route
            path="bug-reports"
            element={<Suspense fallback={<LazyFallback />}><BugReportsPage /></Suspense>}
          />
          <Route
            path="leads"
            element={<Suspense fallback={<LazyFallback />}><LeadsPage /></Suspense>}
          />
          <Route
            path="leads/:id"
            element={<Suspense fallback={<LazyFallback />}><LeadDetailPage /></Suspense>}
          />
          <Route
            path="reports"
            element={<Suspense fallback={<LazyFallback />}><ReportsPage /></Suspense>}
          />
          <Route
            path="attendance"
            element={<Suspense fallback={<LazyFallback />}><AttendancePage /></Suspense>}
          />
          {/* D-319 s.5 - the monthly approval (attendance + all logs) in three steps. */}
          <Route
            path="month-approval/:month"
            element={<Suspense fallback={<LazyFallback />}><MonthApprovalPage /></Suspense>}
          />
          <Route path="profile" element={<ProfileRoute />} />
          {/* D-323: my own bug reports and their status (no screen grant: it is the caller's own data). */}
          <Route path="my-bug-reports" element={<Suspense fallback={<LazyFallback />}><MyBugReportsPage /></Suspense>} />
          <Route
            path="audit"
            element={<Navigate to="/settings/audit-trail" replace />}
          />
          <Route
            path="cashflow"
            element={<Suspense fallback={<LazyFallback />}><CashFlowPage /></Suspense>}
          />
          <Route
            path="api-platform"
            element={<Suspense fallback={<LazyFallback />}><ApiPlatformPage /></Suspense>}
          />
          <Route
            path="integrations"
            element={<Suspense fallback={<LazyFallback />}><IntegrationsPage /></Suspense>}
          />
          <Route
            path="integrations/:id"
            element={<Suspense fallback={<LazyFallback />}><IntegrationDetailPage /></Suspense>}
          />
          <Route
            path="blocklist"
            element={<Suspense fallback={<LazyFallback />}><BlocklistPage /></Suspense>}
          />
          <Route
            path="email-templates"
            element={<Suspense fallback={<LazyFallback />}><EmailTemplatesPage /></Suspense>}
          />
          <Route
            path="settings/audit-trail"
            element={<Suspense fallback={<LazyFallback />}><AuditTrailPage /></Suspense>}
          />
          <Route
            path="settings/system"
            element={<Suspense fallback={<LazyFallback />}><SystemSettingsPage /></Suspense>}
          />
          <Route
            path="settings/backups"
            element={<Suspense fallback={<LazyFallback />}><BackupsPage /></Suspense>}
          />
          <Route
            path="settings/diagnostics"
            element={<Suspense fallback={<LazyFallback />}><AdminDiagnosticsPage /></Suspense>}
          />
          <Route
            path="products"
            element={<Suspense fallback={<LazyFallback />}><ProductsPage /></Suspense>}
          />
          <Route
            path="quotes"
            element={<Suspense fallback={<LazyFallback />}><QuotesPage /></Suspense>}
          />
          <Route
            path="quotes/:id"
            element={<Suspense fallback={<LazyFallback />}><QuoteDetailPage /></Suspense>}
          />
          <Route
            path="permissions"
            element={<Suspense fallback={<LazyFallback />}><PermissionsPage /></Suspense>}
          />
          <Route
            path="portal-access"
            element={<Suspense fallback={<LazyFallback />}><PortalAccessPage /></Suspense>}
          />
          <Route
            path="employee-profiles"
            element={<Suspense fallback={<LazyFallback />}><EmployeeProfilesPage /></Suspense>}
          />
          <Route
            path="engagements"
            element={<Suspense fallback={<LazyFallback />}><EngagementsPage /></Suspense>}
          />
          <Route
            path="engagements/:id"
            element={<Suspense fallback={<LazyFallback />}><EngagementDetailPage /></Suspense>}
          />
          <Route
            path="documents"
            element={<Suspense fallback={<LazyFallback />}><DocumentsPage /></Suspense>}
          />
          <Route
            path="deliveries"
            element={<Suspense fallback={<LazyFallback />}><ProjectDeliveriesPage /></Suspense>}
          />
        </Route>
        </Route>

      <Route
        path="/portal"
        element={
          <CustomerPortalRoute>
            <ErrorBoundary resetKey={location.pathname}>
              <Suspense fallback={<LazyFallback />}>
                <CustomerPortalPage />
              </Suspense>
            </ErrorBoundary>
          </CustomerPortalRoute>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function Root() {
  // The TopBar "חזרה" button needs every page this tab showed, login and portal included.
  useHistoryTrail();
  return (
    <>
      <AppRoutes />
      {/* T-11: always mounted, on every route (login and portal included). */}
      <ToastContainer />
      <LiveAnnouncer />
    </>
  );
}

/** A render error the router caught: shown with the app's own calm fallback. */
function RethrowRouteError(): never {
  throw useRouteError();
}

/**
 * A data router (closing round A4) so `useBlocker` can hold the browser Back
 * button in `useLeaveGuard`. One splat route renders the same `<Routes>` tree
 * as before: no route, guard or path changed. The opt-in flags below only
 * touch fetchers, forms and loaders, which the app does not use.
 */
const router = createBrowserRouter(
  [{ path: '*', element: <Root />, errorElement: <ErrorBoundary><RethrowRouteError /></ErrorBoundary> }],
  {
    future: {
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  },
);

function App() {
  return <RouterProvider router={router} />;
}

export default App;
